import React, { useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  ImagePlus,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "react-toastify";
import { DealsRequest } from "../../requests/vendor/DealsRequest";
import {
  BANNER_ACCEPT,
  BANNER_MAX_SIZE_MB,
  IMAGE_GUIDES,
  MAX_STORE_BANNERS,
  createEmptyBanner,
} from "../../utils/storeDefaults";
import DimensionGuide from "./DimensionGuide";

const BannerUploader = ({ banners = [], onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const sortedBanners = [...banners].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const updateBanner = (id, patch) => {
    onChange(
      sortedBanners.map((banner) => (banner.id === id ? { ...banner, ...patch } : banner))
    );
  };

  const removeBanner = (id) => {
    onChange(sortedBanners.filter((banner) => banner.id !== id));
  };

  const moveBanner = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= sortedBanners.length) return;

    const reordered = [...sortedBanners];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    onChange(reordered.map((banner, order) => ({ ...banner, order })));
  };

  const processFiles = async (files) => {
    if (disabled || uploading) return;

    const fileList = Array.from(files || []);
    if (!fileList.length) return;

    const remainingSlots = MAX_STORE_BANNERS - sortedBanners.length;
    if (remainingSlots <= 0) {
      toast.warn(`You can upload up to ${MAX_STORE_BANNERS} banners.`);
      return;
    }

    const filesToProcess = fileList.slice(0, remainingSlots);
    setUploading(true);

    try {
      const newBanners = [];

      for (const file of filesToProcess) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not a valid image.`);
          continue;
        }

        if (file.size > BANNER_MAX_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} exceeds ${BANNER_MAX_SIZE_MB}MB.`);
          continue;
        }

        const optimized = await DealsRequest.optimizeImage(file);
        newBanners.push({
          ...createEmptyBanner(sortedBanners.length + newBanners.length),
          image: optimized,
        });
      }

      if (newBanners.length) {
        onChange([...sortedBanners, ...newBanners]);
        toast.success(`${newBanners.length} banner${newBanners.length > 1 ? "s" : ""} added.`);
      }
    } catch {
      toast.error("Failed to process banner image.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    processFiles(event.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      <DimensionGuide guide={IMAGE_GUIDES.heroBanner} />

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`relative overflow-hidden rounded-2xl border-2 border-dashed transition-all ${
          isDragging
            ? "border-[#FF6C6C] bg-rose-50/70"
            : "border-gray-200 bg-gray-50/60 hover:border-[#FF6C6C]/40"
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={BANNER_ACCEPT}
          multiple
          className="hidden"
          onChange={(event) => {
            processFiles(event.target.files);
            event.target.value = "";
          }}
        />

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading || sortedBanners.length >= MAX_STORE_BANNERS}
          className="flex flex-col items-center justify-center w-full px-6 py-10 text-center"
        >
          <div className="flex items-center justify-center w-14 h-14 mb-4 rounded-full bg-white shadow-sm text-[#FF6C6C]">
            {uploading ? (
              <Upload className="w-6 h-6 animate-pulse" />
            ) : (
              <ImagePlus className="w-6 h-6" />
            )}
          </div>
          <p className="text-sm font-semibold text-gray-800">
            {uploading ? "Optimizing images..." : "Drop banners here or click to upload"}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {IMAGE_GUIDES.heroBanner.formats} · up to {BANNER_MAX_SIZE_MB}MB ·{" "}
            {sortedBanners.length}/{MAX_STORE_BANNERS} used
          </p>
          <DimensionGuide guide={IMAGE_GUIDES.heroBanner} compact />
        </button>
      </div>

      {sortedBanners.length > 0 ? (
        <div className="space-y-3">
          {sortedBanners.map((banner, index) => (
            <div
              key={banner.id}
              className="flex gap-3 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm"
            >
              <div className="flex flex-col items-center justify-center gap-1 text-gray-400">
                <GripVertical className="w-4 h-4" />
                <button
                  type="button"
                  onClick={() => moveBanner(index, -1)}
                  disabled={index === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveBanner(index, 1)}
                  disabled={index === sortedBanners.length - 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="overflow-hidden w-28 h-20 rounded-xl bg-gray-100 shrink-0">
                {banner.image ? (
                  <img src={banner.image} alt="" className="object-cover w-full h-full" />
                ) : null}
              </div>

              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="text"
                  value={banner.title || ""}
                  onChange={(event) => updateBanner(banner.id, { title: event.target.value })}
                  placeholder="Banner headline (optional)"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
                />
                <input
                  type="text"
                  value={banner.subtitle || ""}
                  onChange={(event) =>
                    updateBanner(banner.id, { subtitle: event.target.value })
                  }
                  placeholder="Supporting text (optional)"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
                />
              </div>

              <button
                type="button"
                onClick={() => removeBanner(banner.id)}
                className="self-start p-2 text-gray-400 rounded-xl hover:text-rose-600 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default BannerUploader;
